import { useMemo } from 'react'

import { useMatch } from 'react-router'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useSpace } from '@tribeplatform/react-sdk/hooks'

import { spaceTreeIndex } from '../tree-items/createSpaceTreeItem.js'

function useActiveSpaceId() {
  const spaceMatch = useMatch(`${adminRoutes.site.root}/spaces/:id/*`)
  return spaceMatch?.params?.id
}

/**
 * Fetches the active space from the route and returns its tree index
 * along with the collection it belongs to (if any).
 *
 * The site tree is rendered in the admin sidebar layout, outside the
 * `spaces/:id` route outlet so it stays visible when no space is selected.
 * Because of this, `useParams` cannot access the space id — `useMatch`
 * lets us read route params from anywhere in the router context.
 *
 * The collection id is fetched so the tree can expand the right collection
 * and ensure the space is visible even when hidden behind pagination.
 */
export function useActiveSpaceData() {
  const spaceId = useActiveSpaceId()

  const { data: space } = useSpace({
    variables: { id: spaceId },
    fields: { collection: 'basic' },
    useQueryOptions: {
      enabled: !!spaceId,
    },
  })

  return useMemo(() => {
    if (!spaceId) {
      return undefined
    }
    return {
      treeIndex: spaceTreeIndex(spaceId),
      collectionId: space?.collection?.id,
    }
  }, [spaceId, space?.collection?.id])
}
